import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Box, Paper, Stack, Typography } from "@mui/material";

import Header from "components/Atoms/SharedComponents/Details/header";
import DetailsList from "components/Atoms/SharedComponents/Details/detailsList";
import { icons } from "assets/AssetHelper";
import { useThemePalette } from "common/hooks/theme_palette";
import {
  formatJsonValue,
  getLogEndpoint,
  getLogMethod,
  getLogOrderReference,
  getLogStatusCode,
  isLogFailed,
} from "templates/merit/utils";

function JsonBlock({ title, value, isError = false }) {
  const { backgroundRed, textRed } = useThemePalette();

  return (
    <Stack gap={1} mb={3}>
      <Typography fontSize="16px" fontWeight={600} fontFamily="Cairo">
        {title}
      </Typography>
      <Paper
        sx={{
          p: 2,
          bgcolor: isError ? backgroundRed : "#f5f5f5",
          overflowX: "auto",
        }}
      >
        <Typography
          component="pre"
          sx={{
            m: 0,
            fontFamily: "monospace",
            fontSize: "13px",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            color: isError ? textRed : "inherit",
          }}
        >
          {formatJsonValue(value)}
        </Typography>
      </Paper>
    </Stack>
  );
}

export default function MeritApiLogDetails({ details }) {
  const { t } = useTranslation();
  const log = details?.api_log ?? details;
  const failed = isLogFailed(log);

  const requestBody =
    log?.request_body ?? log?.request_payload ?? log?.request ?? null;
  const responseBody =
    log?.response_body ?? log?.response_payload ?? log?.response ?? null;
  const errorMessage = log?.error_message ?? log?.error ?? null;

  const metadataSection = {
    head: t("merit_api_logs.log_information"),
    icon: icons?.details,
    details: [
      { label: t("merit_api_logs.col_id"), name: log?.id ?? "-" },
      { label: t("merit_api_logs.col_endpoint"), name: getLogEndpoint(log) },
      { label: t("merit_api_logs.col_method"), name: getLogMethod(log) },
      {
        label: t("merit_api_logs.col_status_code"),
        name: getLogStatusCode(log),
      },
      {
        label: t("merit_api_logs.col_result"),
        name: failed ? t("merit_api_logs.failed") : t("merit_api_logs.success"),
        color: failed ? "error.main" : "success.main",
      },
      {
        label: t("merit_api_logs.col_order"),
        name: getLogOrderReference(log),
      },
      {
        label: t("merit_api_logs.col_date"),
        name: log?.created_at
          ? dayjs(log.created_at).format("DD-MM-YYYY, hh a")
          : "-",
      },
    ],
  };

  return (
    <Stack>
      <Header
        title={t("merit_api_logs.log_details")}
        editBtn={false}
        deleteBtn={false}
      />
      <Stack bgcolor="white" borderRadius="4px" mt={2}>
        <DetailsList data={metadataSection} />
      </Stack>
      <Box mt={2}>
        {errorMessage && (
          <JsonBlock
            title={t("merit_api_logs.error_message")}
            value={errorMessage}
            isError
          />
        )}
        <JsonBlock
          title={t("merit_api_logs.request_body")}
          value={requestBody}
        />
        <JsonBlock
          title={t("merit_api_logs.response_body")}
          value={responseBody}
          isError={failed}
        />
      </Box>
    </Stack>
  );
}
